import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import ClassifyPage from './pages/ClassifyPage';
import BatchPage from './pages/BatchPage';
import TaxPage from './pages/TaxPage';
import HistoryPage from './pages/HistoryPage';
import DashboardPage from './pages/DashboardPage';
import RiskScanPage from './pages/RiskScanPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LandingPage from './pages/LandingPage';

// Redirect unauthenticated users to /login, preserving the intended destination
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        // Splash while token is being validated
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-0">
                <div className="w-8 h-8 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }

    return <>{children}</>;
}

// Redirect already-logged-in users away from login/register
function GuestRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, loading } = useAuth();
    if (loading) return null;
    if (isAuthenticated) return <Navigate to="/dashboard" replace />;
    return <>{children}</>;
}

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <Toaster
                    position="top-right"
                    toastOptions={{
                        style: {
                            background: '#1d1c38',
                            color: '#e2e8f0',
                            border: '1px solid rgba(99,102,241,0.2)',
                            borderRadius: '12px',
                        },
                        success: { iconTheme: { primary: '#10b981', secondary: '#1d1c38' } },
                        error: { iconTheme: { primary: '#ef4444', secondary: '#1d1c38' } },
                    }}
                />
                <Routes>
                    {/* Landing page — public for everyone; Navbar adapts to auth state */}
                    <Route path="/" element={<LandingPage />} />

                    {/* Public auth routes */}
                    <Route path="/login"           element={<GuestRoute><LoginPage /></GuestRoute>} />
                    <Route path="/register"        element={<GuestRoute><RegisterPage /></GuestRoute>} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password"  element={<ResetPasswordPage />} />

                    {/* Protected app shell */}
                    <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="classify" element={<ClassifyPage />} />
                        <Route path="scan" element={<RiskScanPage />} />
                        <Route path="batch" element={<BatchPage />} />
                        <Route path="tax" element={<TaxPage />} />
                        <Route path="history" element={<HistoryPage />} />
                    </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </AuthProvider>
        </BrowserRouter>
    );
}
